import React, { useState, useEffect, useCallback, useRef } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { flashCorrect, flashWrong, Confetti } from '../lib/animations';
import { GameHeader, QuestionCard } from '../lib/GameVisuals';
import { Swords } from 'lucide-react';

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function IceRinkCenter({ t1Score, t2Score, target, hitSide, team1Name, team2Name, goalState }) {
  const puckRef = useRef(null);
  const rinkRef = useRef(null);
  const t1GoalRef = useRef(null);
  const t2GoalRef = useRef(null);
  const goalTextRef = useRef(null);
  const char1Ref = useRef(null); // Team 1 blowing character (top)
  const char2Ref = useRef(null); // Team 2 blowing character (bottom)
  const air1Ref = useRef(null);  // Air particles from top character
  const air2Ref = useRef(null);  // Air particles from bottom character

  // Steps to reach goal from center — keeps each step meaningful regardless of target
  const stepsToGoal = Math.max(3, Math.ceil(target / 2));
  const stepSize = 42 / stepsToGoal; // each step moves puck by this %

  // Normal puck position (tug-of-war): center = 50%, range 8%-92%
  const scoreDiff = t1Score - t2Score;
  const normalTop = 50 + Math.max(-42, Math.min(42, scoreDiff * stepSize));

  // Override position when someone wins — puck goes to the goal
  const isGoal = !!goalState;
  const puckTop = isGoal
    ? (goalState.winner === 'team1' ? 93 : 7)  // slam into opponent's goal
    : normalTop;

  const t1Pct = target > 0 ? Math.min((t1Score / target) * 100, 100) : 0;
  const t2Pct = target > 0 ? Math.min((t2Score / target) * 100, 100) : 0;

  // Normal hit animation + character blowing
  useEffect(() => {
    if (!hitSide || !puckRef.current || isGoal) return;
    const tl = gsap.timeline();
    // Puck reaction
    tl.to(puckRef.current, { scale: 2, duration: 0.12, ease: 'power3.out' });
    tl.to(puckRef.current, { scale: 1, duration: 0.5, ease: 'elastic.out(1.2,0.35)' });
    // Rink shake
    if (rinkRef.current) {
      gsap.fromTo(rinkRef.current, { y: hitSide === 'left' ? -8 : 8 }, { y: 0, duration: 0.4, ease: 'elastic.out(1,0.4)' });
    }

    // Character blowing animation
    const charRef = hitSide === 'left' ? char1Ref.current : char2Ref.current;
    const airRef = hitSide === 'left' ? air1Ref.current : air2Ref.current;
    if (charRef) {
      // Puff up cheeks then blow
      const blowTl = gsap.timeline();
      blowTl.to(charRef, { scaleX: 1.25, scaleY: 0.85, duration: 0.1, ease: 'power2.out' }, 0);
      blowTl.to(charRef, { scaleX: 0.9, scaleY: 1.15, duration: 0.15, ease: 'power3.out' }, 0.1);
      blowTl.to(charRef, { scaleX: 1, scaleY: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' }, 0.25);
    }
    // Air particles shoot out
    if (airRef) {
      gsap.fromTo(airRef, { opacity: 1, scale: 0.5 }, {
        opacity: 0, scale: 2,
        y: hitSide === 'left' ? 100 : -100,
        duration: 0.7, ease: 'power2.out',
      });
    }
  }, [t1Score, t2Score, hitSide, isGoal]);

  // GOAL animation — big dramatic celebration
  useEffect(() => {
    if (!isGoal || !puckRef.current) return;
    const goalRef = goalState.winner === 'team1' ? t2GoalRef.current : t1GoalRef.current;
    const tl = gsap.timeline();
    // Puck slams with massive scale
    tl.to(puckRef.current, { scale: 3, duration: 0.2, ease: 'power4.out' }, 0);
    tl.to(puckRef.current, { scale: 1.3, duration: 0.6, ease: 'elastic.out(1,0.3)' }, 0.2);
    // Rink shakes hard
    if (rinkRef.current) {
      tl.fromTo(rinkRef.current, { y: goalState.winner === 'team1' ? 12 : -12 }, { y: 0, duration: 0.6, ease: 'elastic.out(1,0.3)' }, 0.1);
    }
    // Goal zone flashes rapidly
    if (goalRef) {
      tl.fromTo(goalRef, { opacity: 1, scale: 1.1 }, { opacity: 0.2, scale: 1, duration: 0.12, yoyo: true, repeat: 5 }, 0);
    }
    // GOAL text entrance
    if (goalTextRef.current) {
      tl.fromTo(goalTextRef.current, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(2)' }, 0.15);
    }
  }, [isGoal, goalState]);

  const winnerColor = isGoal ? (goalState.winner === 'team1' ? 'var(--team1)' : 'var(--team2)') : null;

  return (
    <div ref={rinkRef} data-testid="duel-ice-rink" style={{
      width: 280, minWidth: 280, display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
      border: 'none', outline: 'none',
    }}>
      {/* Ice rink surface */}
      <div style={{ position: 'absolute', inset: 0 }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: isGoal
            ? `linear-gradient(180deg, ${goalState.winner === 'team1' ? 'rgba(214,234,248,0.9)' : 'rgba(255,220,220,0.5)'} 0%, #F0F7FC 50%, ${goalState.winner === 'team1' ? 'rgba(255,220,220,0.5)' : 'rgba(214,234,248,0.9)'} 100%)`
            : 'linear-gradient(180deg, rgba(214,234,248,0.9) 0%, #EBF5FB 25%, #F0F7FC 50%, #EBF5FB 75%, rgba(214,234,248,0.9) 100%)',
          borderLeft: '5px solid #444', borderRight: '5px solid #444',
          transition: 'background 0.5s ease',
        }} />
        {/* Top board (Team 1 goal) */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 8, background: 'var(--team1)', zIndex: 2 }} />
        {/* Bottom board (Team 2 goal) */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 8, background: 'var(--team2)', zIndex: 2 }} />
        {/* Center red line */}
        <div style={{ position: 'absolute', top: '50%', left: 5, right: 5, height: 4, background: '#E03131', transform: 'translateY(-50%)', opacity: 0.7 }} />
        {/* Blue lines */}
        <div style={{ position: 'absolute', top: '30%', left: 5, right: 5, height: 2, background: '#1971C2', opacity: 0.35 }} />
        <div style={{ position: 'absolute', top: '70%', left: 5, right: 5, height: 2, background: '#1971C2', opacity: 0.35 }} />
        {/* Center circle */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 100, height: 100, border: '3px solid #1971C2', borderRadius: '50%', opacity: 0.2,
        }} />
        {/* Team 1 goal crease (top) */}
        <div ref={t1GoalRef} style={{
          position: 'absolute', top: 8, left: '10%', right: '10%', height: '14%',
          background: isGoal && goalState.winner === 'team2' ? 'rgba(25,113,194,0.3)' : 'rgba(224,49,49,0.12)',
          borderBottom: '2px dashed rgba(224,49,49,0.3)',
          borderRadius: '0 0 50% 50%',
          transition: 'background 0.3s',
        }} />
        {/* Team 2 goal crease (bottom) */}
        <div ref={t2GoalRef} style={{
          position: 'absolute', bottom: 8, left: '10%', right: '10%', height: '14%',
          background: isGoal && goalState.winner === 'team1' ? 'rgba(224,49,49,0.3)' : 'rgba(25,113,194,0.12)',
          borderTop: '2px dashed rgba(25,113,194,0.3)',
          borderRadius: '50% 50% 0 0',
          transition: 'background 0.3s',
        }} />
        {/* Subtle ice texture lines */}
        {[20, 40, 60, 80].map((t, i) => (
          <div key={i} style={{
            position: 'absolute', top: `${t}%`, left: `${15 + i * 10}%`, width: 40,
            height: 1, background: 'rgba(255,255,255,0.6)', transform: `rotate(${-3 + i * 2}deg)`,
          }} />
        ))}
      </div>

      {/* Team 1 goal label (top, above character) */}
      <div style={{
        position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 9,
        fontFamily: 'Fredoka', fontSize: '0.7rem', fontWeight: 700,
        color: 'white', background: 'var(--team1)',
        padding: '1px 10px', borderRadius: '0.5rem', letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}>{team1Name}</div>

      {/* Team 2 goal label (bottom, below character) */}
      <div style={{
        position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 9,
        fontFamily: 'Fredoka', fontSize: '0.7rem', fontWeight: 700,
        color: 'white', background: 'var(--team2)',
        padding: '1px 10px', borderRadius: '0.5rem', letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}>{team2Name}</div>

      {/* ─── BLOWING CHARACTERS ─── */}
      {/* Team 1 character (top, blows DOWN) */}
      <div ref={char1Ref} style={{
        position: 'absolute', top: '2%', left: '50%', transform: 'translateX(-50%)',
        zIndex: 8, transformOrigin: 'center bottom',
      }}>
        <svg width="96" height="96" viewBox="0 0 48 48">
          {/* Head */}
          <circle cx="24" cy="20" r="16" fill="#FFD8CC" stroke="var(--team1)" strokeWidth="2.5" />
          {/* Cheeks */}
          <circle cx="13" cy="24" r="5" fill="rgba(224,49,49,0.2)" />
          <circle cx="35" cy="24" r="5" fill="rgba(224,49,49,0.2)" />
          {/* Eyes */}
          <circle cx="18" cy="17" r="2.5" fill="#333" />
          <circle cx="30" cy="17" r="2.5" fill="#333" />
          <circle cx="19" cy="16" r="1" fill="white" />
          <circle cx="31" cy="16" r="1" fill="white" />
          {/* Mouth (blowing shape) */}
          <ellipse cx="24" cy="27" rx="4" ry="3.5" fill="#E88" stroke="#C66" strokeWidth="0.8" />
          {/* Little hat/toque */}
          <path d="M10,13 Q14,2 24,1 Q34,2 38,13" fill="var(--team1)" stroke="var(--team1)" strokeWidth="1" />
          <rect x="8" y="12" width="32" height="4" rx="2" fill="white" opacity="0.7" />
          <circle cx="24" cy="1" r="3" fill="white" />
        </svg>
      </div>

      {/* Team 1 air particles (blow downward) */}
      <div ref={air1Ref} style={{
        position: 'absolute', top: '22%', left: '50%', transform: 'translateX(-50%)',
        zIndex: 7, opacity: 0, pointerEvents: 'none',
      }}>
        <svg width="120" height="80" viewBox="0 0 60 40">
          <ellipse cx="30" cy="8" rx="8" ry="4" fill="rgba(200,220,255,0.6)" />
          <ellipse cx="18" cy="16" rx="6" ry="3" fill="rgba(200,220,255,0.5)" />
          <ellipse cx="42" cy="14" rx="5" ry="3" fill="rgba(200,220,255,0.4)" />
          <ellipse cx="30" cy="22" rx="10" ry="4" fill="rgba(200,220,255,0.3)" />
          <ellipse cx="24" cy="30" rx="4" ry="2" fill="rgba(200,220,255,0.2)" />
          <ellipse cx="38" cy="32" rx="5" ry="2" fill="rgba(200,220,255,0.2)" />
        </svg>
      </div>

      {/* Team 2 character (bottom, blows UP) */}
      <div ref={char2Ref} style={{
        position: 'absolute', bottom: '2%', left: '50%', transform: 'translateX(-50%)',
        zIndex: 8, transformOrigin: 'center top',
      }}>
        <svg width="96" height="96" viewBox="0 0 48 48">
          {/* Little hat/toque on top */}
          <path d="M10,17 Q14,6 24,5 Q34,6 38,17" fill="var(--team2)" stroke="var(--team2)" strokeWidth="1" />
          <rect x="8" y="16" width="32" height="4" rx="2" fill="white" opacity="0.7" />
          <circle cx="24" cy="5" r="3" fill="white" />
          {/* Head */}
          <circle cx="24" cy="30" r="16" fill="#FFD8CC" stroke="var(--team2)" strokeWidth="2.5" />
          {/* Cheeks */}
          <circle cx="13" cy="26" r="5" fill="rgba(25,113,194,0.2)" />
          <circle cx="35" cy="26" r="5" fill="rgba(25,113,194,0.2)" />
          {/* Eyes */}
          <circle cx="18" cy="27" r="2.5" fill="#333" />
          <circle cx="30" cy="27" r="2.5" fill="#333" />
          <circle cx="19" cy="26" r="1" fill="white" />
          <circle cx="31" cy="26" r="1" fill="white" />
          {/* Mouth (blowing upward) */}
          <ellipse cx="24" cy="21" rx="4" ry="3.5" fill="#E88" stroke="#C66" strokeWidth="0.8" />
        </svg>
      </div>

      {/* Team 2 air particles (blow upward) */}
      <div ref={air2Ref} style={{
        position: 'absolute', bottom: '22%', left: '50%', transform: 'translateX(-50%)',
        zIndex: 7, opacity: 0, pointerEvents: 'none',
      }}>
        <svg width="120" height="80" viewBox="0 0 60 40">
          <ellipse cx="30" cy="32" rx="8" ry="4" fill="rgba(200,220,255,0.6)" />
          <ellipse cx="18" cy="24" rx="6" ry="3" fill="rgba(200,220,255,0.5)" />
          <ellipse cx="42" cy="26" rx="5" ry="3" fill="rgba(200,220,255,0.4)" />
          <ellipse cx="30" cy="18" rx="10" ry="4" fill="rgba(200,220,255,0.3)" />
          <ellipse cx="24" cy="10" rx="4" ry="2" fill="rgba(200,220,255,0.2)" />
          <ellipse cx="38" cy="8" rx="5" ry="2" fill="rgba(200,220,255,0.2)" />
        </svg>
      </div>

      {/* Progress indicators on sides */}
      <div style={{
        position: 'absolute', left: 7, top: '15%', bottom: '15%', width: 6, zIndex: 4,
        background: 'rgba(224,49,49,0.12)', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', bottom: 0, width: '100%',
          height: `${t1Pct}%`, background: 'var(--team1)', borderRadius: 3,
          transition: 'height 0.5s ease',
        }} />
      </div>
      <div style={{
        position: 'absolute', right: 7, top: '15%', bottom: '15%', width: 6, zIndex: 4,
        background: 'rgba(25,113,194,0.12)', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', bottom: 0, width: '100%',
          height: `${t2Pct}%`, background: 'var(--team2)', borderRadius: 3,
          transition: 'height 0.5s ease',
        }} />
      </div>

      {/* THE PUCK — moves vertically, slams into goal on win */}
      <div ref={puckRef} style={{
        position: 'absolute', left: '50%',
        top: `${puckTop}%`,
        transform: 'translate(-50%, -50%)',
        transition: isGoal ? 'top 0.4s cubic-bezier(0.22, 1, 0.36, 1)' : 'top 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
        zIndex: 10, filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))',
      }}>
        <svg width="44" height="44" viewBox="0 0 36 36">
          <ellipse cx="18" cy="21" rx="15" ry="8" fill="#111" />
          <ellipse cx="18" cy="18" rx="15" ry="8" fill="#1a1a1a" />
          <ellipse cx="18" cy="17" rx="10" ry="5" fill="#333" />
          <ellipse cx="18" cy="16" rx="5" ry="2.5" fill="#555" opacity="0.5" />
        </svg>
      </div>

      {/* GOAL! overlay — shows when a team wins */}
      {isGoal && (
        <div ref={goalTextRef} data-testid="duel-goal-overlay" style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(2px)',
          pointerEvents: 'none',
        }}>
          <div style={{
            fontFamily: 'Fredoka', fontSize: '4rem', fontWeight: 900,
            color: winnerColor, textShadow: '0 4px 12px rgba(0,0,0,0.3), 0 0 40px rgba(255,255,255,0.5)',
            letterSpacing: '0.1em', lineHeight: 1,
          }}>GOAL!</div>
          <div style={{
            fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700,
            color: 'white', marginTop: '0.5rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          }}>{goalState.winnerName} wins!</div>
        </div>
      )}

      {/* "First to X" at center */}
      {!isGoal && (
        <div data-testid="duel-target-label" style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 3, fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 700,
          color: 'rgba(0,0,0,0.35)', letterSpacing: '0.05em',
          pointerEvents: 'none',
          background: 'rgba(255,255,255,0.6)', padding: '2px 10px', borderRadius: '0.75rem',
        }}>First to {target}</div>
      )}
    </div>
  );
}

export function LeDuel({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('duel_conjugaison'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const target = settings.target || 10;

  const validItems = items.filter(i => i.options && i.options.length > 0);
  const t1Items = validItems.filter((_, i) => i % 2 === 0);
  const t2Items = validItems.filter((_, i) => i % 2 === 1);

  const [t1Score, setT1Score] = useState(0);
  const [t2Score, setT2Score] = useState(0);
  const [t1Idx, setT1Idx] = useState(0);
  const [t2Idx, setT2Idx] = useState(0);
  const [t1Fb, setT1Fb] = useState(null);
  const [t2Fb, setT2Fb] = useState(null);
  const [gameTime, setGameTime] = useState(settings.gameTimer || 0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [showConfetti, setShowConfetti] = useState(0);
  const [hitSide, setHitSide] = useState(null);
  const [goalState, setGoalState] = useState(null); // { winner: 'team1'|'team2', winnerName: string }
  const streakRef = useRef(0);
  const timerRef = useRef(null);
  const s1Ref = useRef(null);
  const s2Ref = useRef(null);
  const endedRef = useRef(false);
  const scoresRef = useRef({ t1: 0, t2: 0, answered: 0, correct: 0, streak: 0 });
  const historyRef = useRef([]); // Track all questions for post-game review

  const t1Item = t1Items[t1Idx] || null;
  const t2Item = t2Items[t2Idx] || null;

  // Game-level countdown
  useEffect(() => {
    if (settings.gameTimer <= 0) return;
    timerRef.current = setInterval(() => {
      setGameTime(t => {
        if (t <= 1) { triggerGameEnd(null); return 0; }
        if (t <= 11) playSound('tick');
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Trigger the end sequence — either with a GOAL celebration or immediate finish
  const triggerGameEnd = useCallback((winner) => {
    if (endedRef.current) return;
    endedRef.current = true;
    clearInterval(timerRef.current);

    const s = scoresRef.current;

    // Build full question review: answered + unanswered
    const review = [...historyRef.current];
    // Add unanswered Team 1 questions
    const t1Answered = review.filter(q => q.team === 1).length;
    for (let i = t1Answered; i < t1Items.length; i++) {
      const q = t1Items[i];
      if (!q) break;
      const correctOpt = (q.options || []).find(o => o.correct);
      review.push({
        team: 1, prompt: q.prompt_text_fr || `${q.verb} — ${q.subject} → ?`,
        correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null,
      });
    }
    // Add unanswered Team 2 questions
    const t2Answered = review.filter(q => q.team === 2).length;
    for (let i = t2Answered; i < t2Items.length; i++) {
      const q = t2Items[i];
      if (!q) break;
      const correctOpt = (q.options || []).find(o => o.correct);
      review.push({
        team: 2, prompt: q.prompt_text_fr || `${q.verb} — ${q.subject} → ?`,
        correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null,
      });
    }

    const endData = {
      team1Score: s.t1, team2Score: s.t2,
      totalQuestions: s.answered,
      accuracy: s.answered > 0 ? (s.correct / s.answered) * 100 : 0,
      bestStreak: s.streak,
      questionReview: review,
    };

    if (winner) {
      // GOAL! — show celebration, puck slams into goal, then end after delay
      const winnerName = winner === 'team1' ? settings.team1Name : settings.team2Name;
      setGoalState({ winner, winnerName });
      setShowConfetti(c => c + 1);
      playSound('goal');
      // Let students see the GOAL celebration for 2.5 seconds
      setTimeout(() => onGameEnd(endData), 2500);
    } else {
      // Timer ran out or no more questions — no goal, just finish
      setShowConfetti(c => c + 1);
      playSound('win');
      setTimeout(() => onGameEnd(endData), 1500);
    }
  }, [onGameEnd, playSound, settings.team1Name, settings.team2Name]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => triggerGameEnd(null);
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [triggerGameEnd]);

  // Win condition check
  useEffect(() => {
    if (endedRef.current) return;
    if (t1Score >= target) {
      triggerGameEnd('team1');
    } else if (t2Score >= target) {
      triggerGameEnd('team2');
    } else if (!t1Item && !t2Item) {
      // Ran out of questions — winner is whoever has more
      const winner = scoresRef.current.t1 > scoresRef.current.t2 ? 'team1'
        : scoresRef.current.t2 > scoresRef.current.t1 ? 'team2' : null;
      triggerGameEnd(winner);
    }
  }, [t1Score, t2Score, t1Item, t2Item, target, triggerGameEnd]);

  const handleAnswer = useCallback((team, optIdx, btnEl) => {
    if (goalState) return; // Frozen during goal celebration
    const item = team === 1 ? t1Item : t2Item;
    if (!item || (team === 1 && t1Fb) || (team === 2 && t2Fb)) return;
    const correct = item.options[optIdx]?.correct;
    const correctOpt = (item.options || []).find(o => o.correct);
    setTotalAnswered(a => { const v = a + 1; scoresRef.current.answered = v; return v; });
    if (correct) {
      // Record correct answer in history
      historyRef.current.push({
        team, prompt: item.prompt_text_fr || `${item.verb} — ${item.subject} → ?`,
        correctAnswer: correctOpt?.text || '—', userAnswer: item.options[optIdx]?.text || '', wasCorrect: true,
      });
      playSound('correct');
      setTotalCorrect(c => { const v = c + 1; scoresRef.current.correct = v; return v; });
      streakRef.current += 1;
      setBestStreak(b => { const v = Math.max(b, streakRef.current); scoresRef.current.streak = v; return v; });
      if (btnEl) flashCorrect(btnEl);
      setHitSide(team === 1 ? 'left' : 'right');
      setTimeout(() => setHitSide(null), 600);
      if (team === 1) {
        setT1Score(s => { const v = s + 1; scoresRef.current.t1 = v; return v; });
        setT1Fb('correct'); setTimeout(() => { setT1Fb(null); setT1Idx(i => i + 1); }, 600);
      } else {
        setT2Score(s => { const v = s + 1; scoresRef.current.t2 = v; return v; });
        setT2Fb('correct'); setTimeout(() => { setT2Fb(null); setT2Idx(i => i + 1); }, 600);
      }
    } else {
      playSound('wrong'); streakRef.current = 0; if (btnEl) flashWrong(btnEl);
      if (team === 1) { setT1Fb('wrong'); setTimeout(() => setT1Fb(null), 500); }
      else { setT2Fb('wrong'); setTimeout(() => setT2Fb(null), 500); }
    }
  }, [t1Item, t2Item, t1Fb, t2Fb, playSound, goalState]);

  return (
    <div data-testid="le-duel-game" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', overflow: 'hidden' }}>
      <Confetti trigger={showConfetti} />

      <GameHeader title="LE DUEL" icon={<Swords size={28} color="var(--team1)" />} color="var(--team1)">
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{ background: 'var(--team1)', color: 'white', padding: '0.2rem 1rem', borderRadius: '2rem', fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700 }}>{settings.team1Name}</span>
            <span ref={s1Ref} className="font-score" style={{ fontSize: '2.8rem', fontWeight: 800, color: 'var(--team1)' }} data-testid="duel-team1-score">{t1Score}</span>
          </div>
          {settings.gameTimer > 0 && (
            <div className={`font-score ${gameTime <= 10 ? 'timer-critical' : ''}`}
              style={{ fontSize: '2.2rem', fontWeight: 800, color: gameTime <= 10 ? 'var(--error)' : 'var(--accent)' }}>
              {formatTime(gameTime)}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span ref={s2Ref} className="font-score" style={{ fontSize: '2.8rem', fontWeight: 800, color: 'var(--team2)' }} data-testid="duel-team2-score">{t2Score}</span>
            <span style={{ background: 'var(--team2)', color: 'white', padding: '0.2rem 1rem', borderRadius: '2rem', fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700 }}>{settings.team2Name}</span>
          </div>
        </div>
      </GameHeader>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <DuelSide team={1} item={t1Item} fb={t1Fb} onAnswer={handleAnswer} color="var(--team1)" bg="var(--team1-bg)" frozen={!!goalState} remaining={target - t1Score} target={target} />
        <IceRinkCenter t1Score={t1Score} t2Score={t2Score} target={target} hitSide={hitSide} team1Name={settings.team1Name} team2Name={settings.team2Name} goalState={goalState} />
        <DuelSide team={2} item={t2Item} fb={t2Fb} onAnswer={handleAnswer} color="var(--team2)" bg="var(--team2-bg)" frozen={!!goalState} remaining={target - t2Score} target={target} />
      </div>
    </div>
  );
}

function DuelSide({ team, item, fb, onAnswer, color, bg, frozen, remaining, target }) {
  const isAlmost = remaining === 1;
  if (!item) {
    return (
      <div style={{ flex: 1, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.5rem', color: 'var(--text-muted)', textAlign: 'center', padding: '1.5rem' }}>
          All done! Waiting...
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`duel-side-t${team}`} style={{
      flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: '1.5rem 2rem', background: bg, gap: '0.75rem',
      opacity: frozen ? 0.5 : 1, transition: 'opacity 0.3s',
      pointerEvents: frozen ? 'none' : 'auto',
    }}>
      {/* "X left to win" counter */}
      <div data-testid={`duel-t${team}-remaining`} style={{
        textAlign: 'center', marginBottom: '0.25rem',
      }}>
        <span style={{
          fontFamily: 'Fredoka', fontSize: isAlmost ? '1.3rem' : '1.1rem', fontWeight: 800,
          color: color,
          background: isAlmost ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.75)',
          padding: isAlmost ? '4px 18px' : '3px 14px', borderRadius: '2rem',
          border: isAlmost ? `2.5px solid ${color}` : '1.5px solid rgba(0,0,0,0.08)',
          boxShadow: isAlmost ? `0 0 16px ${color}33` : 'none',
          animation: isAlmost ? 'pulseGlow 1s ease-in-out infinite' : 'none',
          display: 'inline-block',
        }}>
          {remaining > 0 ? `${remaining} left to win` : 'Winner!'}
        </span>
        <style>{`@keyframes pulseGlow { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }`}</style>
      </div>

      <QuestionCard accentColor={color} promptImage={item.prompt_image}
        promptFr={item.prompt_text_fr || `${item.verb} — ${item.subject} → ?`}
        answer={(item.options || []).find(o => o.correct)?.text}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {(item.options || []).map((opt, idx) => (
          <button key={`${item.item_id}-${idx}`} data-testid={`duel-opt-t${team}-${idx}`}
            className="option-btn" onClick={(e) => onAnswer(team, idx, e.currentTarget)}
            disabled={fb === 'correct' || frozen} style={{ borderLeftColor: color, borderLeftWidth: 5, fontSize: '1.35rem', minHeight: 58, padding: '0.75rem 1.25rem' }}>
            {opt.text}
          </button>
        ))}
      </div>
    </div>
  );
}
